import React, { useEffect, useState } from 'react';
import moment from 'moment';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Info } from '@mui/icons-material';
import { Tooltip, Typography, Box, Stack } from '@mui/material';

import CTabPanel, { CTab, CTabs } from 'components/reusable/CTabPanel';
import MainCard from 'ui-component/cards/MainCard';
import DailyReport from './dailyReport';
import MonthlyReport from './monthlyReport';

import useAuth from 'hooks/useAuth';
import { ADDT_TYPE_LOCATION_ID, CLASS } from 'constant';
import {
    useLazyDownloadAttendanceReportsQuery,
    useLazyDownloadMonthlyAttendanceReportsQuery,
    useLazyGetStudentsDailyReportQuery,
    useLazyGetStudentsMonthlyReportQuery
} from 'store/services/school/session';
import { v4 as uuidv4 } from 'uuid';

const dailyInitialValues = {
    startDate: null,
    location: null,
    classroom: null,
    student: '',
    status: null
};

const monthlyInitialValues = {
    month: null,
    location: null,
    classroom: null,
    student: ''
};

const validationSchemaDaily = Yup.object({
    startDate: Yup.date().nullable().required('Please select a date.'),
    location: Yup.object().nullable().required('Please select a location.')
});

const validationSchemaMonthly = Yup.object({
    month: Yup.date().nullable().required('Please select a month.'),
    location: Yup.object().nullable().required('Please select a location.')
});

const Tabs = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState(0);
    const [dailyPage, setDailyPage] = useState(1);
    const [monthlyPage, setMonthlyPage] = useState(1);

    const [monthlyReportData, setMonthlyReportData] = useState(null);
    const [dailyReportData, setDailyReportData] = useState(null);

    const [triggerDailyList, { data: dailyData, isLoading: isDailyLoading, isFetching: isDailyFetching }] =
        useLazyGetStudentsDailyReportQuery();
    const [triggerMonthlyList, { data: monthlyData, isLoading: isMonthlyLoading, isFetching: isMonthlyFetching }] =
        useLazyGetStudentsMonthlyReportQuery();

    const [download, { isFetching: downloadLoading }] = useLazyDownloadAttendanceReportsQuery();
    const [downloadMonthly, { isFetching: downloadMonthlyLoading }] = useLazyDownloadMonthlyAttendanceReportsQuery();

    const formikDaily = useFormik({
        initialValues: dailyInitialValues,
        validationSchema: validationSchemaDaily,
        onSubmit: async (values) => {
            const filters = {
                client_id: user?.client_id,
                location_id: values?.location?.id,
                // eslint-disable-next-line dot-notation
                service: values.location?.location_type?.location_type === 'EIDT' ? CLASS.eidt : CLASS.addt,
                status: values?.status?.id,
                start_date: moment(new Date(values?.startDate)).startOf('day').utc().format('YYYY-MM-DD HH:mm'),
                end_date: moment(new Date(values?.startDate)).endOf('day').utc().format('YYYY-MM-DD HH:mm'),
                ...(values?.student && { student_name: values?.student }),
                ...(values?.classroom && { class_room_id: values?.classroom?.id }),
                is_daily_report: 1
            };
            const resp = await triggerDailyList({
                filters,
                pagination: {
                    current_page: parseInt(dailyPage, 10)
                }
            });
            setDailyReportData(resp?.data); // Reset monthly report data when daily report is fetched
        }
    });

    const formikMonthly = useFormik({
        initialValues: monthlyInitialValues,
        validationSchema: validationSchemaMonthly,
        onSubmit: async (values) => {
            const selectedDate = new Date(values.month); // this is a Date object
            const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1, 0, 0, 0);
            const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0, 23, 59, 59);

            const filters = {
                client_id: user?.client_id,
                location_id: values?.location?.id,
                service: values.location?.location_type?.location_type === 'EIDT' ? CLASS.eidt : CLASS.addt,
                // status: values?.status?.id,
                ...(values?.student && { student_name: values?.student }),
                ...(values?.classroom && { class_room_id: values?.classroom?.id }),
                start_date: moment(new Date(startOfMonth)).startOf('day').format('YYYY-MM-DD HH:mm'),
                end_date: moment(new Date(endOfMonth)).endOf('day').format('YYYY-MM-DD HH:mm')
            };
            const resp = await triggerMonthlyList({
                filters,
                pagination: {
                    current_page: parseInt(monthlyPage, 10)
                }
            });
            // ✅ Add UUID to each row in fields
            const dataWithUUID = {
                ...resp?.data,
                fields: resp?.data?.fields?.map((item) => ({
                    ...item,
                    _uuid: uuidv4() // Add unique key
                }))
            };

            setMonthlyReportData(dataWithUUID);
            // setMonthlyReportData(resp?.data); // Reset daily report data when monthly report is fetched
        }
    });

    useEffect(() => {
        if (formikDaily.values.startDate && formikDaily.values.location) {
            formikDaily.submitForm();
        }
    }, [dailyPage]);

    useEffect(() => {
        if (formikMonthly.values.month && formikMonthly.values.location) {
            formikMonthly.submitForm();
        }
    }, [monthlyPage]);

    return (
        <MainCard
            sx={{ paddingTop: 0 }}
            title={
                <Stack direction="row" spacing={2} sx={{
                    alignItems: "center"
                }}>
                    <Typography variant="h3">Attendance Report</Typography>
                    <Tooltip
                        enterTouchDelay={0}
                        title="The report includes the attendance list of Day Center students, both day-wise and month-wise"
                    >
                        <Info
                            sx={{
                                cursor: 'pointer',
                                height: 20,
                                width: 20,
                                color: (theme) => theme.palette.grey[400]
                            }}
                        />
                    </Tooltip>
                </Stack>
            }
        >
            <Box>
                <CTabs value={activeTab} onChange={(_, val) => setActiveTab(val)}>
                    <CTab label="Daily" value={0} />
                    <CTab label="Monthly" value={1} />
                </CTabs>
            </Box>
            <CTabPanel value={activeTab} index={0}>
                <DailyReport
                    activeTab={activeTab}
                    formik={formikDaily}
                    dailyReportData={dailyReportData}
                    dailyReportLoading={isDailyLoading || isDailyFetching}
                    setPage={setDailyPage}
                    page={dailyPage}
                    triggerDailyList={triggerDailyList}
                    download={download}
                    downloading={downloadLoading}
                    setDailyReportData={setDailyReportData}
                />
            </CTabPanel>
            <CTabPanel value={activeTab} index={1}>
                <MonthlyReport
                    activeTab={activeTab}
                    formik={formikMonthly}
                    monthlyReportData={monthlyReportData}
                    monthlyReportLoading={isMonthlyLoading || isMonthlyFetching}
                    setPage={setMonthlyPage}
                    page={monthlyPage}
                    triggerDailyList={triggerMonthlyList}
                    download={downloadMonthly}
                    downloading={downloadMonthlyLoading}
                    setMonthlyReportData={setMonthlyReportData}
                />
            </CTabPanel>
        </MainCard>
    );
};

export default Tabs;
